/**
 * Contracts Page - T165
 *
 * List view of all contracts with filtering
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { GlassPanel, Button, Input, LoadingSpinner, EmptyState, Pagination } from '../components/ui';
import { ContractCard } from '../components/contracts/ContractCard';
import { useContracts, type ContractStatus } from '../hooks/useContracts';

export const Route = createFileRoute('/contracts')({
  component: ContractsPage,
});

const statusOptions: Array<{ value: ContractStatus | ''; label: string }> = [
  { value: '', label: 'Alle Status' },
  { value: 'draft', label: 'Entwurf' },
  { value: 'active', label: 'Aktiv' },
  { value: 'paused', label: 'Pausiert' },
  { value: 'completed', label: 'Abgeschlossen' },
  { value: 'cancelled', label: 'Storniert' },
];

function ContractsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ContractStatus | ''>('');

  const { data, isLoading, error } = useContracts({
    page,
    limit: 12,
    search: search || undefined,
    status: status || undefined,
    sortBy: 'createdAt',
    sortDirection: 'desc',
  });

  const contracts = data?.data || [];
  const pagination = data?.pagination;

  return (
    <ProtectedRoute>
      <AppShell>
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-white">Verträge</h1>
            <p className="text-white/50 mt-1">Vertragsverwaltung mit wiederkehrender Abrechnung</p>
          </div>
          <Button onClick={() => navigate({ to: '/contracts/new' })}>
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Neuer Vertrag
          </Button>
        </div>

        {/* Filters */}
        <GlassPanel className="mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Suche nach Vertragsnummer oder Titel..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setStatus(option.value);
                    setPage(1);
                  }}
                  className={`
                    px-3 py-1.5 rounded-full text-sm font-medium transition-colors
                    ${status === option.value
                      ? 'bg-white/10 text-white'
                      : 'text-white/50 hover:text-white hover:bg-white/5'
                    }
                  `}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </GlassPanel>

        {/* Content */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : error ? (
          <GlassPanel>
            <p className="text-red-400 text-center py-8">Fehler beim Laden der Verträge</p>
          </GlassPanel>
        ) : contracts.length === 0 ? (
          <EmptyState
            icon={
              <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"
                />
              </svg>
            }
            title={search || status ? 'Keine Verträge gefunden' : 'Noch keine Verträge'}
            description={
              search || status
                ? 'Versuchen Sie eine andere Suche oder entfernen Sie Filter'
                : 'Erstellen Sie Ihren ersten Vertrag für wiederkehrende Rechnungen'
            }
            action={
              !search && !status
                ? { label: 'Vertrag erstellen', onClick: () => navigate({ to: '/contracts/new' }) }
                : undefined
            }
          />
        ) : (
          <>
            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {contracts.map((contract) => (
                <ContractCard key={contract.id} contract={contract} />
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </AppShell>
    </ProtectedRoute>
  );
}
